import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import * as React from "react";
import {useEffect, useState} from "react";
import {Document, Page, pdfjs} from "react-pdf";
import "../styles/CustomPdf.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function CustomPdf({src, showDownload = true, showPrint = true}) {
    const [numPages, setNumPages] = useState<number>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [scale, setScale] = useState<number>(2.3);

    useEffect(() => {
        const updateZoom = () => {
            if (window.innerWidth <= 768) {
                setScale(1.0);
            } else {
                setScale(1.8);
            }
        };

        // Set initial width
        updateZoom();

        // Add event listener for window resize
        window.addEventListener("resize", updateZoom);
        return () => {
            // Clean up the event listener on component unmount
            window.removeEventListener("resize", updateZoom);
        };
    }, []);

    function onDocumentLoadSuccess({numPages}: { numPages: number }): void {
        setNumPages(numPages);
    }

    function nextPage() {
        setPageNumber((v) => ++v);
    }

    function prevPage() {
        setPageNumber((v) => --v);
    }

    function handleZoomChange(event: React.ChangeEvent<HTMLInputElement>) {
        setScale(parseFloat(event.target.value));
    }

    function zoomIn() {
        setScale((prevScale) => Math.min(prevScale + 0.1, 3.0)); // Max zoom level: 2.0
    }

    function zoomOut() {
        setScale((prevScale) => Math.max(prevScale - 0.1, 0.25)); // Min zoom level: 0.5
    }

    function downloadPdf() {
        const link = document.createElement("a");
        link.href = src;
        link.download = "document.pdf";
        link.click();
    }

    function printPdf() {
        const printWindow = window.open(src, "_blank");
        if (printWindow) {
            printWindow.focus();
            printWindow.print();
        }
    }

    return (
        <div className="pdf-container">
            <div className="pdf-header">
                <h2>PDF Viewer</h2>
                <div className="pdf-buttons">
                    <button onClick={prevPage} disabled={pageNumber <= 1}>
                        <i className="fa-solid fa-angle-left"></i>
                    </button>
                    <button onClick={nextPage} disabled={pageNumber >= (numPages ?? -1)}>
                        <i className="fa-solid fa-angle-right"></i>
                    </button>
                    {showDownload && (
                        <button onClick={downloadPdf}>
                            <i className="fa-solid fa-download"></i>
                        </button>
                    )}
                    {showPrint && (
                        <button onClick={printPdf}>
                            <i className="fa-solid fa-print"></i>
                        </button>
                    )}
                </div>
                <label htmlFor="zoom" className="zoom-label">
                    <span>Zoom: </span>
                    <span>{(scale * 100).toFixed(0)}%</span>
                </label>
                <div className="zoom-bar">
                    <i className="fa-solid fa-search-minus" onClick={zoomOut}></i>
                    <input
                        id="zoom"
                        type="range"
                        min="0.5"
                        max="3.0"
                        step="0.1"
                        value={scale}
                        onChange={handleZoomChange}
                    />
                    <i className="fa-solid fa-search-plus" onClick={zoomIn}></i>
                </div>
            </div>
            <div className="pdf-content">
                <Document
                    file={src}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className="my-react-pdf"
                >
                    <Page pageNumber={pageNumber} scale={scale}/>
                </Document>
            </div>
            <p className="pdf-page-info">
                Page {pageNumber} of {numPages}
            </p>
        </div>
    );
}